import { Center, Loader } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";

import { useIdentityStore } from "@mountayaapp/theme";

import { resetIdentification } from "../analytics/rules/identify";
import { ory } from "../auth/rules/ory";

/**
 * Route is /logout: a flat action route (no _auth layout). It opens an Ory
 * logout browser flow and submits it over AJAX with the logout_token. The
 * Accept: application/json header makes Ory return 204 No Content (rather than a
 * 303 redirect that fetch would then have to follow), terminating the session
 * and clearing the ory_session_* cookie through the same localhost tunnel origin
 * the SDK talks to. Navigating to flow.logout_url instead does not reliably
 * clear the localhost cookie under the local `ory tunnel`. It then resets the
 * Identity store and does a full-page redirect to /login (the reload busts
 * ensureSession's module-scoped cache and starts a fresh login flow). A
 * no-session/error path converges on the same reset + redirect.
 */
export const Route = createFileRoute("/logout")({
  component: LogoutRoute,
});

function LogoutRoute() {
  const reset = useIdentityStore((state) => state.reset);

  useEffect(() => {
    void (async () => {
      try {
        const flow = await ory.createBrowserLogoutFlow();
        await ory.updateLogoutFlow(
          { token: flow.logout_token },
          { headers: { Accept: "application/json" } },
        );
      } catch {
        // No active session (or the flow could not be created): already logged
        // out — fall through to the redirect below.
      } finally {
        reset();
        // Unlink the device before the redirect, not after: the assign below is a
        // full page load, so anything deferred past it never runs and the next
        // person to sign in here would inherit this identity.
        resetIdentification();
        globalThis.window?.location.assign("/login");
      }
    })();
  }, [reset]);

  return (
    <Center mih="100vh">
      <Loader />
    </Center>
  );
}
